import React from "react";

//Common
import DisplayData from "../../common/DisplayData/DisplayData";
import Logo from "../../../assets/images/logo.png";
import FooterLogo from "../../../assets/images/cauw-footer.png";
import { getFormattedDate } from "../../../utility/lib/date";

//App
import { EmployeePledgeSummaryProps, PaymentOption } from "../../../types";
import { getFormattedCurrency, getLookupValue } from "../../../utility/helpers";
import {
  AGENCY_OTHER_OPTION_ID,
  DATA_PRIVACY_TEXT,
  GIFT_INVESTMENT_OPTION_OTHER_ID,
  TAX_TEXT,
  TAX_TEXT_STYLE,
  TAX_TEXT_CONFIRMATION_STYLE,
} from "./constants";
import usePledgeStyles, { usePrintStyles } from "./styles";

const PrintPledgeSummary = React.forwardRef(
  (props: EmployeePledgeSummaryProps, ref: any) => {
    const { summary, lookups, isConfirmation } = props;
    const {
      contributionAmount,
      paymentOptionId,
      isNotWillingToContribute,
      organizationContactName,
      organizationContactEmail,
      submitDate,
      payrollDeductionFrequency,
      spouseName,
      spousePledgeAmount,
      spouseWorkPlace,
      isWillingToStayAnonymous,
      isWillingToCombineSpouseContribution,
      giftInvestment,
      isRecurringContribution,
    } = summary;

    const summaryStyles = usePledgeStyles();
    const printStyles = usePrintStyles();
    const {
      paymentOption,
      agencies,
      giftInvestment: giftInvestmentLookup,
    } = lookups ?? {};

    const paymentOptionValue = getLookupValue(paymentOptionId, paymentOption);

    const hasPaymentOption = paymentOptionId && paymentOptionValue;

    const isPayrollDeduction =
      hasPaymentOption && paymentOptionId === PaymentOption.PayrollDeduction;

    const isCreditCard =
      hasPaymentOption && paymentOptionId === PaymentOption.CreditCard;

    const isCheck = hasPaymentOption && paymentOptionId === PaymentOption.Check;

    let methodOfGiving = null;
    if (hasPaymentOption) {
      methodOfGiving = paymentOptionValue;
      if (isPayrollDeduction && payrollDeductionFrequency) {
        methodOfGiving += ` - ${payrollDeductionFrequency}`;
      } else if (
        isCreditCard &&
        contributionAmount &&
        isRecurringContribution
      ) {
        methodOfGiving += `: 12 monthly payments of ${getFormattedCurrency(
          Number((contributionAmount / 12).toFixed(2))
        )}`; //Bug-15950
      } else if (isCheck) {
        methodOfGiving +=
          " - Capital Area United Way, 700 Laurel Street, Baton Rouge, LA 70802";
      }
    }

    const hasSpouseContribution =
      isWillingToCombineSpouseContribution &&
      spouseName &&
      spouseWorkPlace &&
      spousePledgeAmount;

    const printedAt = new Date().toLocaleString([], {
      dateStyle: "short",
      timeStyle: "short",
    });

    const agenciesList = giftInvestment?.map((x) => {
      if (x.giftInvestmentId === GIFT_INVESTMENT_OPTION_OTHER_ID) {
        return {
          name:
            x.agencyId === AGENCY_OTHER_OPTION_ID
              ? x.otherAgencyName
              : getLookupValue(x.agencyId, agencies),
          amount: x.pledgeAmount,
        };
      } else {
        return {
          name: getLookupValue(x.giftInvestmentId, giftInvestmentLookup),
          amount: x.pledgeAmount,
        };
      }
    });

    const EMAIL_CAMPAIGN_REPRESENTATIVE_TEXT = (
      <p>
        If any of this information is incorrect, please email your campaign
        representative {organizationContactName} at{" "}
        <a href={`mailto:${organizationContactEmail}`}>
          {organizationContactEmail}
        </a>
      </p>
    );

    const showEmailCampaignRepresentativeText =
      organizationContactName && organizationContactEmail;

    const marginTop = "10px";
    const marginRight = "20px";
    const marginBottom = "70px";
    const marginLeft = "20px";
    const getPageStyles = () => {
      return `@page { margin: ${marginTop} ${marginRight} ${marginBottom} ${marginLeft} !important; } 
    .tbl-responsive table {
      border-collapse: collapse;
      display: flex;
      width: 100%;
    }
    th, td {
      padding: 4px;
      text-align: left;
      border-bottom: 1px solid #ddd;
    }
    th {
      background-color: #f2f2f2;
    }
    .article { page-break-before: auto; }
    `;
    };

    return (
      <div
        className="d-flex align-items-center justify-content-center mt-5"
        ref={ref}
      >
        <style>{getPageStyles()}</style>
        <table className={printStyles.container}>
            <thead>
              <tr>
                <td style={{ border: "none" }}>
                  <h1>
                    <img src={Logo} alt="" className="img-fluid header-logo" />
                  </h1>
                </td>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={{ border: "none" }}>
                      <h5>Pledge Details</h5>
                </td>
              </tr>
              {isNotWillingToContribute ? (
                <>
                  <tr>
                    <td style={{ border: "none" }}>
                      <DisplayData
                        text="Response"
                        importedData="I do not wish to pledge or contribute at this time."
                      />
                    </td>
                  </tr>
                  <tr>
                    <td style={{ border: "none" }}>
                      <DisplayData text="Pledge Amount" importedData="$0" />
                    </td>
                  </tr>
                </>
              ) : (
                <>
              {submitDate && (
                <tr>
                  <td style={{ border: "none" }}>
                    <DisplayData
                      text="Pledge Date"
                      importedData={getFormattedDate(submitDate?.toString())}
                    />
                  </td>
                </tr>
              )}
              <tr>
                <td style={{ border: "none" }}>
                  <DisplayData
                    text="Pledge Amount"
                    importedData={`${
                      contributionAmount
                        ? getFormattedCurrency(contributionAmount)
                        : "$" + 0
                    }`}
                  />
                </td>
              </tr>
              {hasPaymentOption && (
                <tr>
                  <td style={{ border: "none" }}>
                    <DisplayData
                      text="Method of Giving"
                      importedData={methodOfGiving}
                    ></DisplayData>
                  </td>
                </tr>
              )}
              {agenciesList && agenciesList?.length > 0 && (
                <tr>
                  <td style={{ border: "none" }}>
                    <>
                      <div
                        className="flex-shrink-1"
                        style={summaryStyles.boldLabel}
                      >
                        Designations:
                      </div>
                      <ul style={{ marginBottom: 0 }}>
                        {agenciesList
                          ?.filter((x) => x?.amount !== 0)
                          ?.map((agency, index) => (
                            <li key={index}>{`${agency.name} : ${
                              agency.amount
                                ? getFormattedCurrency(agency.amount)
                                : "$" + 0
                            }`}</li>
                          ))}
                      </ul>
                    </>
                  </td>
                </tr>
              )}
              {hasSpouseContribution && (
                <tr>
                  <td style={{ border: "none" }}>
                    <DisplayData
                      text="Spouse/Significant Other"
                      importedData={`${spouseName} - ${spouseWorkPlace} : ${
                        spousePledgeAmount
                          ? getFormattedCurrency(spousePledgeAmount)
                          : "$" + 0
                      }`}
                    />
                  </td>
                </tr>
              )}
              <tr>
                <td style={{ border: "none" }}>
                  <DisplayData
                    text="Anonymous Donation"
                    importedData={
                      isWillingToStayAnonymous?.toString() === "1"
                        ? "Yes"
                        : "No"
                    }
                  />
                </td>
              </tr>
                </>
              )}
              {showEmailCampaignRepresentativeText && (
                <tr>
                  <td style={{ border: "none" }}>
                    <div
                      style={{
                        ...TAX_TEXT_STYLE,
                        marginTop: 40,
                        fontSize: "medium",
                        paddingRight: "10%",
                      }}
                    >
                      {EMAIL_CAMPAIGN_REPRESENTATIVE_TEXT}
                    </div>
                  </td>
                </tr>
              )}
              <tr>
                <td style={{ border: "none" }}>
                  <div
                    style={
                      isConfirmation
                        ? { ...TAX_TEXT_CONFIRMATION_STYLE, marginTop: 40 }
                        : {
                            ...TAX_TEXT_STYLE,
                            marginTop: 40,
                            paddingRight: "10%",
                          }
                    }
                  >
                    {TAX_TEXT}
                    <br />
                    <br />
                    {DATA_PRIVACY_TEXT}
                  </div>
                </td>
              </tr>
              <tr>
                <td style={{ border: "none" }}>
                  <div className="print-pledge-footer" style={printStyles.footer}>
                    <img
                      src={FooterLogo}
                      alt="Capital Area United Way"
                      style={printStyles.footerLogo}
                    />
                    <span style={printStyles.footerText}>
                      Generated by Capital Area United Way on {printedAt}
                    </span>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
      </div>
    );
  }
);

export default PrintPledgeSummary;
